import React, { useEffect, useState, useRef, Component } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, ArrowRight, ArrowLeft, Wind, BarChart3, TrendingUp, Zap, Check, AlertTriangle, Trophy, ChevronLeft, ChevronRight, Eye, Bell, Mail, MessageSquare, Shield, MapPin, Thermometer, Sun, Droplets, ChevronDown, Play, Globe, X, LayoutGrid, PenLine } from 'lucide-react';
import { AreaChart, Area, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ComposedChart, Cell, ReferenceLine } from 'recharts';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { useUserProfile } from '../hooks/useUserProfile';
import { useTheme } from '../hooks/useTheme';
// --- Mock Data ---
const windData = [{
  time: '00:00',
  value: 15.2
}, {
  time: '04:00',
  value: 16.8
}, {
  time: '08:00',
  value: 18.5
}, {
  time: '12:00',
  value: 22.1
}, {
  time: '14:00',
  value: 24.5
}, {
  time: '16:00',
  value: 23.8
}, {
  time: '20:00',
  value: 20.4
}, {
  time: '00:00',
  value: 18.2
}];
const compareData = [{
  time: '0h',
  ept2: 15.2,
  ifs: 15.0
}, {
  time: '3h',
  ept2: 15.8,
  ifs: 15.5
}, {
  time: '6h',
  ept2: 17.1,
  ifs: 16.9
}, {
  time: '9h',
  ept2: 19.4,
  ifs: 19.1
}, {
  time: '12h',
  ept2: 22.1,
  ifs: 21.8
}, {
  time: '15h',
  ept2: 21.3,
  ifs: 20.8
}, {
  time: '18h',
  ept2: 19.6,
  ifs: 19.7
}, {
  time: '21h',
  ept2: 18.4,
  ifs: 18.9
}, {
  time: '24h',
  ept2: 18.2,
  ifs: 18.5
}, {
  time: '27h',
  ept2: 20.1,
  ifs: 19.2
}, {
  time: '30h',
  ept2: 22.8,
  ifs: 21.0
}, {
  time: '33h',
  ept2: 24.9,
  ifs: 22.1
}, {
  time: '36h',
  ept2: 26.4,
  ifs: 23.2
}, {
  time: '39h',
  ept2: 27.6,
  ifs: 23.8
}, {
  time: '42h',
  ept2: 28.1,
  ifs: 24.2
}, {
  time: '45h',
  ept2: 27.3,
  ifs: 24.5
}, {
  time: '48h',
  ept2: 26.0,
  ifs: 24.1
}];
// 7-day history (obs + both model runs) + 2-day forecast
// EPT2-HRRR tracks observations closely; IFS drifts more
const modelComparisonData = [
// Past 7 days — all three lines overlap
{
  day: 'Mar 5',
  observed: 18.4,
  hrrr: 18.1,
  ifs: 17.2
}, {
  day: 'Mar 6',
  observed: 15.9,
  hrrr: 16.2,
  ifs: 17.5
}, {
  day: 'Mar 7',
  observed: 20.3,
  hrrr: 20.0,
  ifs: 18.1
}, {
  day: 'Mar 8',
  observed: 24.7,
  hrrr: 24.3,
  ifs: 21.8
}, {
  day: 'Mar 9',
  observed: 21.6,
  hrrr: 21.9,
  ifs: 19.5
}, {
  day: 'Mar 10',
  observed: 17.8,
  hrrr: 17.5,
  ifs: 19.4
}, {
  day: 'Mar 11',
  observed: 19.2,
  hrrr: 19.0,
  ifs: 17.1
},
// Today — last observation + forecast continues
{
  day: 'Today',
  observed: 22.5,
  hrrr: 22.3,
  ifs: 20.4
},
// Future 2 days — forecast only
{
  day: 'Mar 13',
  hrrr: 25.1,
  ifs: 22.0
}, {
  day: 'Mar 14',
  hrrr: 23.4,
  ifs: 20.6
}];
// Historical benchmark: 10 days past + 3 days future, run initialized at Day 0 00z
const historicalBenchmarkData = [
// Past — all three lines present (observed + both forecasts)
{
  day: 'Mar 1',
  observed: 14.8,
  ept2: 14.5,
  ifs: 13.9
}, {
  day: 'Mar 2',
  observed: 16.2,
  ept2: 16.0,
  ifs: 15.1
}, {
  day: 'Mar 3',
  observed: 19.5,
  ept2: 19.8,
  ifs: 18.2
}, {
  day: 'Mar 4',
  observed: 22.1,
  ept2: 21.7,
  ifs: 19.8
}, {
  day: 'Mar 5',
  observed: 18.4,
  ept2: 18.8,
  ifs: 20.5
}, {
  day: 'Mar 6',
  observed: 15.9,
  ept2: 15.6,
  ifs: 17.2
}, {
  day: 'Mar 7',
  observed: 20.3,
  ept2: 20.0,
  ifs: 18.1
}, {
  day: 'Mar 8',
  observed: 24.7,
  ept2: 24.2,
  ifs: 21.8
}, {
  day: 'Mar 9',
  observed: 21.6,
  ept2: 21.9,
  ifs: 19.5
}, {
  day: 'Mar 10',
  observed: 17.8,
  ept2: 17.5,
  ifs: 19.1
},
// Today — last observation + forecast starts
{
  day: 'Today',
  observed: 19.2,
  ept2: 19.0,
  ifs: 17.4
},
// Future — forecast only (no observations yet)
{
  day: 'Mar 12',
  ept2: 22.4,
  ifs: 20.1
}, {
  day: 'Mar 13',
  ept2: 25.8,
  ifs: 22.6
}, {
  day: 'Mar 14',
  ept2: 23.1,
  ifs: 20.8
}];
const energyData = [{
  time: 'Mon 00',
  wind: 11.2,
  price: 88
}, {
  time: 'Mon 06',
  wind: 12.0,
  price: 85
}, {
  time: 'Mon 12',
  wind: 13.5,
  price: 78
}, {
  time: 'Mon 18',
  wind: 12.8,
  price: 82
}, {
  time: 'Tue 00',
  wind: 14.1,
  price: 74
}, {
  time: 'Tue 06',
  wind: 15.0,
  price: 70
}, {
  time: 'Tue 12',
  wind: 17.2,
  price: 62
}, {
  time: 'Tue 18',
  wind: 16.5,
  price: 66
}, {
  time: 'Wed 00',
  wind: 19.8,
  price: 55
}, {
  time: 'Wed 06',
  wind: 22.1,
  price: 48
}, {
  time: 'Wed 12',
  wind: 24.5,
  price: 45
}, {
  time: 'Wed 18',
  wind: 23.2,
  price: 47
}, {
  time: 'Thu 00',
  wind: 20.4,
  price: 56
}, {
  time: 'Thu 06',
  wind: 18.0,
  price: 65
}, {
  time: 'Thu 12',
  wind: 16.8,
  price: 72
}, {
  time: 'Thu 18',
  wind: 15.2,
  price: 76
}, {
  time: 'Fri 00',
  wind: 12.5,
  price: 84
}, {
  time: 'Fri 06',
  wind: 10.0,
  price: 95
}, {
  time: 'Fri 12',
  wind: 11.4,
  price: 90
}, {
  time: 'Fri 18',
  wind: 13.1,
  price: 80
}];
// --- Helper Components ---
function Typewriter({
  text,
  delay = 30
}: {
  text: string;
  delay?: number;
}) {
  const [displayedText, setDisplayedText] = useState('');
  useEffect(() => {
    setDisplayedText('');
    let i = 0;
    const timer = setInterval(() => {
      if (i < text.length) {
        setDisplayedText(prev => prev + text.charAt(i));
        i++;
      } else {
        clearInterval(timer);
      }
    }, delay);
    return () => clearInterval(timer);
  }, [text, delay]);
  return <span data-id="element-2688">{displayedText}</span>;
}
function AthenaBubble({
  children
}: {
  children: React.ReactNode;
}) {
  return <motion.div initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.4
  }} className="flex gap-4 max-w-2xl mx-auto w-full" data-id="element-2689">
      <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 shadow-[0_0_15px_rgba(236,72,153,0.3)] mt-1" data-id="element-2690">
        <img src="https://cdn.magicpatterns.com/uploads/vnVWXWFT6UXMq6P6HiyM1D/pasted-image.png" alt="Athena" className="w-full h-full object-cover" data-id="element-2691" />
      </div>
      <div className="bg-white/[0.04] border border-white/[0.08] rounded-2xl rounded-tl-none p-5 text-slate-200 text-lg leading-relaxed flex-1 shadow-lg" data-id="element-2692">
        {children}
      </div>
    </motion.div>;
}
function InsightCard({
  icon: Icon,
  title,
  children
}: {
  icon: any;
  title: string;
  children: React.ReactNode;
}) {
  return <motion.div initial={{
    opacity: 0,
    scale: 0.95
  }} animate={{
    opacity: 1,
    scale: 1
  }} transition={{
    delay: 2.4,
    duration: 0.4
  }} className="bg-[#EC4899]/10 border border-[#EC4899]/20 rounded-xl p-4 mt-6 max-w-2xl mx-auto w-full flex gap-4 items-start" data-id="element-2693">
      <div className="p-2 bg-[#EC4899]/20 rounded-lg text-[#EC4899] flex-shrink-0" data-id="element-2694">
        <Icon size={20} data-id="element-2695" />
      </div>
      <div data-id="element-2696">
        <h4 className="text-white font-semibold mb-1" data-id="element-2697">{title}</h4>
        <div className="text-slate-300 text-sm" data-id="element-2698">{children}</div>
      </div>
    </motion.div>;
}
function CapabilityTag({
  text
}: {
  text: string;
}) {
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} transition={{
    delay: 2.8,
    duration: 0.5
  }} className="flex justify-center mt-8" data-id="element-2699">
      <Badge variant="outline" className="bg-white/[0.03] border-white/[0.1] text-slate-400 px-3 py-1.5" data-id="element-2700">
        <Sparkles size={12} className="mr-2 text-[#EC4899]" data-id="element-2701" />
        Capability revealed:{' '}
        <span className="text-slate-300 ml-1 font-medium" data-id="element-2702">{text}</span>
      </Badge>
    </motion.div>;
}
// --- Main Component ---
export function AthenaDiscoveryView({
  embedded = false
}: {
  embedded?: boolean;
}) {
  const navigate = useNavigate();
  const {
    theme
  } = useTheme();
  const isLight = theme === 'light';
  const {
    profile
  } = useUserProfile();
  const zone = profile?.defaultBiddingZone || 'DE-LU';
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const mainRef = useRef<HTMLElement>(null);
  const goToStep = (newStep: number) => {
    setDirection(newStep > step ? 1 : -1);
    setStep(newStep);
    // Scroll main content area to top on step change
    requestAnimationFrame(() => {
      mainRef.current?.scrollTo({
        top: 0
      });
    });
  };
  const handleComplete = () => {
    navigate('/athena');
  };
  const variants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 40 : -40,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      x: direction < 0 ? 40 : -40,
      opacity: 0
    })
  };
  // --- Render Steps ---
  const renderStep1 = () => <div className="space-y-12 flex flex-col items-center justify-center min-h-[400px]" data-id="element-2703">
      <div className="w-16 h-16 rounded-full overflow-hidden shadow-[0_0_30px_rgba(236,72,153,0.4)] mb-4" data-id="element-2704">
        <img src="https://cdn.magicpatterns.com/uploads/oDxLcC3FkLNQvi2zVEpXDm/pasted-image.png" alt="Athena" className="w-full h-full object-cover" data-id="element-2705" />
      </div>

      <div className="text-center space-y-4 max-w-2xl" data-id="element-2706">
        <h1 className="text-3xl md:text-4xl font-bold text-white tracking-tight min-h-[3.5rem]" data-id="element-2707">
          <Typewriter text="Hi! I'm Athena, your AI energy intelligence assistant." delay={40} data-id="element-2708" />
        </h1>
        <motion.p initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 2.5,
        duration: 0.8
      }} className="text-slate-400 text-lg leading-relaxed" data-id="element-2709">
          I can analyze forecasts, compare models, benchmark accuracy, and
          connect weather to energy markets — all through conversation.
        </motion.p>
      </div>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 3.5,
      duration: 0.8
    }} className="grid grid-cols-3 gap-6 w-full max-w-2xl" data-id="element-2710">
        {[{
        icon: Wind,
        label: 'Forecasts'
      }, {
        icon: BarChart3,
        label: 'Models'
      }, {
        icon: Zap,
        label: 'Markets'
      }].map((item, i) => <div key={i} className="flex flex-col items-center gap-3 p-6 rounded-2xl bg-white/[0.02] border border-white/[0.05]" data-id="element-2711">
            <item.icon size={28} className="text-[#EC4899]" data-id="element-2712" />
            <span className="text-sm font-medium text-slate-300" data-id="element-2713">
              {item.label}
            </span>
          </div>)}
      </motion.div>
    </div>;
  const renderStep2 = () => <div className="w-full flex flex-col items-center" data-id="element-2714">
      <AthenaBubble data-id="element-2715">
        <Typewriter text={`Here's the latest wind generation outlook for ${zone}.`} delay={20} data-id="element-2716" />
      </AthenaBubble>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 1.2,
      duration: 0.6
    }} className="w-full max-w-2xl mt-8 bg-white/[0.02] border border-white/[0.08] rounded-2xl p-6" data-id="element-2717">
        <h3 className="text-sm font-medium text-slate-400 mb-6" data-id="element-2718">
          Wind Generation (GW) • Next 48h
        </h3>
        <div className="h-48 w-full" data-id="element-2719">
          <ResponsiveContainer width="100%" height="100%" data-id="element-2720">
            <AreaChart data={windData} margin={{
            top: 5,
            right: 0,
            left: -20,
            bottom: 0
          }} data-id="element-2721">
              <defs data-id="element-2722">
                <linearGradient id="colorWind" x1="0" y1="0" x2="0" y2="1" data-id="element-2723">
                  <stop offset="5%" stopColor="#EC4899" stopOpacity={0.3} data-id="element-2724" />
                  <stop offset="95%" stopColor="#EC4899" stopOpacity={0} data-id="element-2725" />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" vertical={false} data-id="element-2726" />
              <XAxis dataKey="time" stroke="rgba(255,255,255,0.3)" fontSize={11} tickLine={false} axisLine={false} data-id="element-2727" />
              <YAxis stroke="rgba(255,255,255,0.3)" fontSize={11} tickLine={false} axisLine={false} data-id="element-2728" />
              <Tooltip contentStyle={{
              backgroundColor: '#0F172A',
              borderColor: 'rgba(255,255,255,0.1)',
              borderRadius: '8px'
            }} itemStyle={{
              color: '#EC4899'
            }} data-id="element-2729" />
              <Area type="monotone" dataKey="value" name="Wind Generation" unit=" GW" stroke="#EC4899" strokeWidth={2} fillOpacity={1} fill="url(#colorWind)" data-id="element-2730" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </motion.div>

      <InsightCard icon={Zap} title="Peak Generation Alert" data-id="element-2731">
        Wind generation peaks at <strong data-id="element-2732">24.5 GW</strong> tomorrow at 14:00 —
        15% above the seasonal norm.
      </InsightCard>

      <CapabilityTag text="Market aggregate forecasts with MW output" data-id="element-2733" />
    </div>;
  const renderStep3 = () => <div className="w-full flex flex-col items-center" data-id="element-2734">
      <AthenaBubble data-id="element-2735">
        <Typewriter text="Here's our hourly EPT2-HRRR model vs IFS, overlaid with observations for the past week." delay={20} data-id="element-2736" />
      </AthenaBubble>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 1.0,
      duration: 0.6
    }} className={`w-full max-w-2xl mt-8 border rounded-2xl p-6 ${isLight ? 'bg-slate-50 border-slate-200' : 'bg-white/[0.02] border-white/[0.08]'}`} data-id="element-2737">
        <div className="flex items-center justify-between mb-2" data-id="element-2738">
          <h3 className={`text-sm font-medium ${isLight ? 'text-slate-600' : 'text-slate-400'}`} data-id="element-2739">
            Wind Generation (GW) • 7-day history + 2-day forecast
          </h3>
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs mb-5" data-id="element-2740">
          <span className={`flex items-center gap-1.5 ${isLight ? 'text-slate-600' : 'text-slate-300'}`} data-id="element-2741">
            <div className={`w-3 h-0 border-t-2 border-dashed ${isLight ? 'border-slate-400' : 'border-white/45'}`} data-id="element-2742" />{' '}
            Observed
          </span>
          <span className={`flex items-center gap-1.5 ${isLight ? 'text-slate-600' : 'text-slate-300'}`} data-id="element-2743">
            <div className="w-2 h-2 rounded-full bg-[#8b5cf6]" data-id="element-2744" /> EPT2-HRRR
          </span>
          <span className={`flex items-center gap-1.5 ${isLight ? 'text-slate-600' : 'text-slate-300'}`} data-id="element-2745">
            <div className="w-2 h-2 rounded-full bg-blue-500" data-id="element-2746" /> EC IFS
          </span>
        </div>

        <div className="h-56 w-full" data-id="element-2747">
          <ResponsiveContainer width="100%" height="100%" data-id="element-2748">
            <LineChart data={modelComparisonData} margin={{
            top: 5,
            right: 10,
            left: -20,
            bottom: 0
          }} data-id="element-2749">
              <CartesianGrid strokeDasharray="3 3" stroke={isLight ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.1)'} vertical={false} data-id="element-2750" />
              <XAxis dataKey="day" stroke={isLight ? 'rgba(0,0,0,0.4)' : 'rgba(255,255,255,0.3)'} fontSize={10} tickLine={false} axisLine={false} data-id="element-2751" />
              <YAxis stroke={isLight ? 'rgba(0,0,0,0.4)' : 'rgba(255,255,255,0.3)'} fontSize={11} tickLine={false} axisLine={false} domain={[14, 28]} data-id="element-2752" />
              <Tooltip contentStyle={{
              backgroundColor: isLight ? '#ffffff' : '#0F172A',
              borderColor: isLight ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
              borderRadius: '8px',
              fontSize: '12px',
              color: isLight ? '#334155' : undefined
            }} labelStyle={{
              color: isLight ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.5)',
              marginBottom: 4
            }} data-id="element-2753" />
              {/* Today reference line */}
              <ReferenceLine x="Today" stroke={isLight ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.25)'} strokeDasharray="4 4" label={{
              value: 'Today',
              position: 'top',
              fill: isLight ? 'rgba(0,0,0,0.45)' : 'rgba(255,255,255,0.5)',
              fontSize: 10
            }} data-id="element-2754" />
              {/* Observed */}
              <Line type="monotone" dataKey="observed" name="Observed" stroke={isLight ? 'rgba(0,0,0,0.35)' : 'rgba(255,255,255,0.45)'} strokeWidth={2.5} strokeDasharray="4 4" dot={false} activeDot={{
              r: 5,
              fill: isLight ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.7)',
              strokeWidth: 0
            }} connectNulls={false} data-id="element-2755" />
              {/* EPT2-HRRR — violet/purple, tracks closest to observed */}
              <Line type="monotone" dataKey="hrrr" name="EPT2-HRRR" stroke="#8b5cf6" strokeWidth={2} dot={false} activeDot={{
              r: 4,
              fill: '#8b5cf6',
              strokeWidth: 0
            }} data-id="element-2756" />
              {/* EC IFS — drifts further */}
              <Line type="monotone" dataKey="ifs" name="EC IFS" stroke="#3b82f6" strokeWidth={2} dot={false} activeDot={{
              r: 4,
              fill: '#3b82f6',
              strokeWidth: 0
            }} data-id="element-2757" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Performance highlight */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 1.8,
        duration: 0.5
      }} className="mt-4 p-3 rounded-xl bg-[#8b5cf6]/[0.08] border border-[#8b5cf6]/20 flex items-center gap-3" data-id="element-2758">
          <div className="w-8 h-8 rounded-lg bg-[#8b5cf6]/20 flex items-center justify-center flex-shrink-0" data-id="element-2759">
            <Trophy size={16} className="text-[#8b5cf6]" data-id="element-2760" />
          </div>
          <div className="text-xs text-slate-300" data-id="element-2761">
            <span className="text-[#8b5cf6] font-semibold" data-id="element-2762">EPT2-HRRR</span>{' '}
            tracks observations closely with{' '}
            <span className="text-white font-semibold" data-id="element-2763">0.35 GW MAE</span> vs IFS
            at 2.1 GW — best performance in the 4–5 day range.
          </div>
        </motion.div>
      </motion.div>

      <InsightCard icon={AlertTriangle} title="Model Divergence Detected" data-id="element-2764">
        EPT2-HRRR and IFS diverge by <strong data-id="element-2765">3.1 GW</strong> on Mar 13. IFS
        consistently overshoots or undershoots — EPT2-HRRR stays tight.
      </InsightCard>

      <CapabilityTag text="Hourly EPT2-HRRR model comparison" data-id="element-2766" />
    </div>;
  const [alertCreated, setAlertCreated] = useState(false);
  const renderStepAlert = () => <div className="w-full flex flex-col items-center" data-id="element-2767">
      <AthenaBubble data-id="element-2768">
        <Typewriter text="Want me to alert you when models diverge like this? I can set that up instantly." delay={20} data-id="element-2769" />
      </AthenaBubble>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 1.2,
      duration: 0.6
    }} className="w-full max-w-2xl mt-8 bg-white/[0.02] border border-white/[0.08] rounded-2xl p-6" data-id="element-2770">
        <div className="flex items-center gap-3 mb-5" data-id="element-2771">
          <div className="p-2 rounded-lg bg-amber-500/15 text-amber-400" data-id="element-2772">
            <Bell size={18} data-id="element-2773" />
          </div>
          <div data-id="element-2774">
            <h3 className="text-sm font-semibold text-white" data-id="element-2775">
              Model Divergence Alert
            </h3>
            <p className="text-[11px] text-slate-500" data-id="element-2776">
              Pre-configured from your analysis
            </p>
          </div>
        </div>

        <div className="space-y-3" data-id="element-2777">
          {/* Condition */}
          <div className="flex items-center justify-between p-3 rounded-xl bg-white/[0.03] border border-white/[0.06]" data-id="element-2778">
            <div data-id="element-2779">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1" data-id="element-2780">
                Condition
              </div>
              <div className="text-sm text-slate-200" data-id="element-2781">
                EPT2-HRRR vs EC IFS spread exceeds
              </div>
            </div>
            <div className="text-lg font-bold text-amber-400" data-id="element-2782">3.0 GW</div>
          </div>

          {/* Region & Variable */}
          <div className="grid grid-cols-2 gap-3" data-id="element-2783">
            <div className="p-3 rounded-xl bg-white/[0.03] border border-white/[0.06]" data-id="element-2784">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1" data-id="element-2785">
                Region
              </div>
              <div className="text-sm text-slate-200" data-id="element-2786">{zone}</div>
            </div>
            <div className="p-3 rounded-xl bg-white/[0.03] border border-white/[0.06]" data-id="element-2787">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1" data-id="element-2788">
                Variable
              </div>
              <div className="text-sm text-slate-200" data-id="element-2789">Wind Generation</div>
            </div>
          </div>

          {/* Horizon */}
          <div className="p-3 rounded-xl bg-white/[0.03] border border-white/[0.06]" data-id="element-2790">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1" data-id="element-2791">
              Forecast Horizon
            </div>
            <div className="text-sm text-slate-200" data-id="element-2792">
              Hours 24–48 (peak divergence window)
            </div>
          </div>

          {/* Delivery */}
          <div className="p-3 rounded-xl bg-white/[0.03] border border-white/[0.06]" data-id="element-2793">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2" data-id="element-2794">
              Deliver via
            </div>
            <div className="flex gap-2" data-id="element-2795">
              {[{
              icon: Mail,
              label: 'Email',
              active: true
            }, {
              icon: Bell,
              label: 'Push',
              active: false
            }].map(({
              icon: Icon,
              label,
              active
            }) => <div key={label} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium ${active ? 'bg-[#EC4899]/10 text-[#EC4899] border border-[#EC4899]/20' : 'bg-white/[0.03] text-slate-500 border border-white/[0.06]'}`} data-id="element-2796">
                  <Icon size={12} data-id="element-2797" />
                  {label}
                </div>)}
            </div>
          </div>
        </div>

        {/* Create Alert Button */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 1.8,
        duration: 0.4
      }} className="mt-5" data-id="element-2798">
          {!alertCreated ? <button onClick={() => setAlertCreated(true)} className={`w-full py-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 hover:scale-[1.01] transition-all ${isLight ? 'bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 hover:border-amber-300 hover:shadow-md hover:shadow-amber-100/50' : 'bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:shadow-lg hover:shadow-amber-500/20'}`} data-id="element-2799">
              <Bell size={16} data-id="element-2800" />
              Create Divergence Alert
            </button> : <motion.div initial={{
          scale: 0.95,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} className="w-full py-3 rounded-xl bg-emerald-500/15 border border-emerald-500/25 text-emerald-400 font-semibold text-sm flex items-center justify-center gap-2" data-id="element-2801">
              <Check size={16} data-id="element-2802" />
              Alert Created — You'll be notified on next divergence
            </motion.div>}
        </motion.div>
      </motion.div>

      <InsightCard icon={Shield} title="Proactive Risk Monitoring" data-id="element-2803">
        Athena monitors model spreads continuously. You'll get alerted{' '}
        <strong data-id="element-2804">before</strong> uncertainty impacts your positions.
      </InsightCard>

      <CapabilityTag text="AI-powered alert creation from analysis" data-id="element-2805" />
    </div>;
  const [benchRegion, setBenchRegion] = useState(zone);
  const [benchVariable, setBenchVariable] = useState('wind_100m');
  const [benchModels, setBenchModels] = useState<string[]>(['ept2-hrrr', 'ifs']);
  const [benchRan, setBenchRan] = useState(false);
  const toggleModel = (m: string) => {
    setBenchModels(prev => prev.includes(m) ? prev.filter(x => x !== m) : [...prev, m]);
  };
  const regions = [{
    id: 'DE-LU',
    label: 'Germany (DE-LU)'
  }, {
    id: 'FR',
    label: 'France (FR)'
  }, {
    id: 'NL',
    label: 'Netherlands (NL)'
  }, {
    id: 'ES',
    label: 'Spain (ES)'
  }, {
    id: 'NO-2',
    label: 'Norway South (NO-2)'
  }, {
    id: 'DK-1',
    label: 'Denmark West (DK-1)'
  }];
  const variables = [{
    id: 'wind_100m',
    label: 'Wind 100m',
    icon: Wind
  }, {
    id: 'wind_10m',
    label: 'Wind 10m',
    icon: Wind
  }, {
    id: 'solar_ghi',
    label: 'Solar GHI',
    icon: Sun
  }, {
    id: 'temperature',
    label: 'Temperature 2m',
    icon: Thermometer
  }, {
    id: 'precipitation',
    label: 'Precipitation',
    icon: Droplets
  }];
  const models = [{
    id: 'ept2-hrrr',
    label: 'EPT2-HRRR',
    color: '#8b5cf6',
    desc: 'Best 0–5 day'
  }, {
    id: 'ept2-global',
    label: 'EPT2-Global',
    color: '#EC4899',
    desc: 'Best 5–10 day'
  }, {
    id: 'ifs',
    label: 'EC IFS',
    color: '#3b82f6',
    desc: 'ECMWF baseline'
  }, {
    id: 'gfs',
    label: 'GFS',
    color: '#94a3b8',
    desc: 'NCEP baseline'
  }, {
    id: 'icon',
    label: 'ICON-EU',
    color: '#f59e0b',
    desc: 'DWD regional'
  }];
  // 12-day history + 2-day forecast benchmark data
  const benchChartData = [{
    day: 'Mar 1',
    observed: 14.8,
    'ept2-hrrr': 14.5,
    'ept2-global': 14.9,
    ifs: 13.9,
    gfs: 13.2,
    icon: 14.1
  }, {
    day: 'Mar 2',
    observed: 16.2,
    'ept2-hrrr': 16.0,
    'ept2-global': 16.4,
    ifs: 15.1,
    gfs: 14.5,
    icon: 15.8
  }, {
    day: 'Mar 3',
    observed: 19.5,
    'ept2-hrrr': 19.3,
    'ept2-global': 19.0,
    ifs: 18.2,
    gfs: 17.1,
    icon: 18.9
  }, {
    day: 'Mar 4',
    observed: 22.1,
    'ept2-hrrr': 21.8,
    'ept2-global': 21.5,
    ifs: 19.8,
    gfs: 18.9,
    icon: 20.8
  }, {
    day: 'Mar 5',
    observed: 18.4,
    'ept2-hrrr': 18.6,
    'ept2-global': 18.1,
    ifs: 20.5,
    gfs: 21.2,
    icon: 19.1
  }, {
    day: 'Mar 6',
    observed: 15.9,
    'ept2-hrrr': 16.1,
    'ept2-global': 15.5,
    ifs: 17.2,
    gfs: 18.0,
    icon: 16.4
  }, {
    day: 'Mar 7',
    observed: 20.3,
    'ept2-hrrr': 20.1,
    'ept2-global': 19.8,
    ifs: 18.1,
    gfs: 17.4,
    icon: 19.5
  }, {
    day: 'Mar 8',
    observed: 24.7,
    'ept2-hrrr': 24.4,
    'ept2-global': 23.9,
    ifs: 21.8,
    gfs: 20.5,
    icon: 23.2
  }, {
    day: 'Mar 9',
    observed: 21.6,
    'ept2-hrrr': 21.8,
    'ept2-global': 21.2,
    ifs: 19.5,
    gfs: 18.8,
    icon: 20.6
  }, {
    day: 'Mar 10',
    observed: 17.8,
    'ept2-hrrr': 17.6,
    'ept2-global': 17.2,
    ifs: 19.1,
    gfs: 20.3,
    icon: 18.0
  }, {
    day: 'Mar 11',
    observed: 19.2,
    'ept2-hrrr': 19.0,
    'ept2-global': 18.6,
    ifs: 17.1,
    gfs: 16.2,
    icon: 18.4
  }, {
    day: 'Today',
    observed: 22.5,
    'ept2-hrrr': 22.3,
    'ept2-global': 21.8,
    ifs: 20.4,
    gfs: 19.1,
    icon: 21.5
  }, {
    day: 'Mar 13',
    'ept2-hrrr': 25.1,
    'ept2-global': 24.2,
    ifs: 22.0,
    gfs: 20.8,
    icon: 23.5
  }, {
    day: 'Mar 14',
    'ept2-hrrr': 23.4,
    'ept2-global': 22.5,
    ifs: 20.6,
    gfs: 19.2,
    icon: 21.8
  }];
  const benchmarkResults: Record<string, {
    mae: string;
    rank: number;
  }> = {
    'ept2-hrrr': {
      mae: '0.35',
      rank: 1
    },
    'ept2-global': {
      mae: '0.52',
      rank: 2
    },
    ifs: {
      mae: '1.68',
      rank: 3
    },
    gfs: {
      mae: '2.14',
      rank: 4
    },
    icon: {
      mae: '1.42',
      rank: 2
    }
  };
  const renderStep4 = () => <div className="w-full flex flex-col items-center" data-id="element-2806">
      <AthenaBubble data-id="element-2807">
        <Typewriter text="Now let's set up a custom benchmark. Pick your region, variable, and models — I'll run the numbers." delay={20} data-id="element-2808" />
      </AthenaBubble>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 1.2,
      duration: 0.6
    }} className="w-full max-w-2xl mt-8 bg-white/[0.02] border border-white/[0.08] rounded-2xl p-6" data-id="element-2809">
        <h3 className="text-sm font-medium text-slate-400 mb-5 flex items-center gap-2" data-id="element-2810">
          <BarChart3 size={14} className="text-[#EC4899]" data-id="element-2811" />
          Benchmark Configuration
        </h3>

        <div className="space-y-4" data-id="element-2812">
          {/* Region Selector */}
          <div data-id="element-2813">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5" data-id="element-2814">
              <Globe size={10} data-id="element-2815" /> Region / Bidding Zone
            </div>
            <div className="flex flex-wrap gap-2" data-id="element-2816">
              {regions.map(r => <button key={r.id} onClick={() => {
              setBenchRegion(r.id);
              setBenchRan(false);
            }} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${benchRegion === r.id ? 'bg-[#EC4899]/15 text-[#EC4899] border border-[#EC4899]/25' : 'bg-white/[0.03] text-slate-400 border border-white/[0.06] hover:text-slate-200 hover:bg-white/[0.05]'}`} data-id="element-2817">
                  {r.label}
                </button>)}
            </div>
          </div>

          {/* Variable Selector */}
          <div data-id="element-2818">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5" data-id="element-2819">
              <Thermometer size={10} data-id="element-2820" /> Weather Variable
            </div>
            <div className="flex flex-wrap gap-2" data-id="element-2821">
              {variables.map(v => {
              const VIcon = v.icon;
              return <button key={v.id} onClick={() => {
                setBenchVariable(v.id);
                setBenchRan(false);
              }} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${benchVariable === v.id ? 'bg-[#EC4899]/15 text-[#EC4899] border border-[#EC4899]/25' : 'bg-white/[0.03] text-slate-400 border border-white/[0.06] hover:text-slate-200 hover:bg-white/[0.05]'}`} data-id="element-2822">
                    <VIcon size={12} data-id="element-2823" />
                    {v.label}
                  </button>;
            })}
            </div>
          </div>

          {/* Model Selector */}
          <div data-id="element-2824">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5" data-id="element-2825">
              <BarChart3 size={10} data-id="element-2826" /> Models to Compare
            </div>
            <div className="flex flex-wrap gap-2" data-id="element-2827">
              {models.map(m => {
              const selected = benchModels.includes(m.id);
              return <button key={m.id} onClick={() => {
                toggleModel(m.id);
                setBenchRan(false);
              }} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-all ${selected ? 'border bg-white/[0.04]' : 'bg-white/[0.02] text-slate-500 border border-white/[0.06] hover:text-slate-300 hover:bg-white/[0.04]'}`} style={selected ? {
                borderColor: m.color + '40',
                color: m.color
              } : {}} data-id="element-2828">
                    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{
                  backgroundColor: selected ? m.color : 'rgba(255,255,255,0.15)'
                }} data-id="element-2829" />
                    <span data-id="element-2830">{m.label}</span>
                    <span className={`text-[10px] ${selected ? 'opacity-60' : 'text-slate-600'}`} data-id="element-2831">
                      {m.desc}
                    </span>
                  </button>;
            })}
            </div>
          </div>
        </div>

        {/* Run Benchmark Button / Results */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 1.8,
        duration: 0.4
      }} className="mt-6" data-id="element-2832">
          {!benchRan ? <button onClick={() => setBenchRan(true)} disabled={benchModels.length < 2} className={`w-full py-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all ${benchModels.length >= 2 ? 'bg-gradient-to-r from-[#EC4899] to-[#8b5cf6] text-white hover:shadow-lg hover:shadow-pink-500/20 hover:scale-[1.01]' : 'bg-white/[0.05] text-slate-500 cursor-not-allowed'}`} data-id="element-2833">
              <Play size={16} data-id="element-2834" />
              Run Benchmark • {benchModels.length} models ×{' '}
              {regions.find(r => r.id === benchRegion)?.label}
            </button> : <motion.div initial={{
          opacity: 0,
          scale: 0.97
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="space-y-4" data-id="element-2835">
              {/* Benchmark Chart — 12 days history + 2 days forecast */}
              <div data-id="element-2836">
                <div className="flex items-center justify-between mb-2" data-id="element-2837">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500" data-id="element-2838">
                    {variables.find(v => v.id === benchVariable)?.label} •{' '}
                    {benchRegion} • 12d + 2d forecast
                  </div>
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px] mb-3" data-id="element-2839">
                  <span className="flex items-center gap-1 text-slate-400" data-id="element-2840">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" data-id="element-2841" />{' '}
                    Observed
                  </span>
                  {benchModels.map(mId => {
                const m = models.find(x => x.id === mId);
                if (!m) return null;
                return <span key={mId} className="flex items-center gap-1 text-slate-400" data-id="element-2842">
                        <div className="w-1.5 h-1.5 rounded-full" style={{
                    backgroundColor: m.color
                  }} data-id="element-2843" />{' '}
                        {m.label}
                      </span>;
              })}
                </div>
                <div className="h-44 w-full" data-id="element-2844">
                  <ResponsiveContainer width="100%" height="100%" data-id="element-2845">
                    <LineChart data={benchChartData} margin={{
                  top: 5,
                  right: 10,
                  left: -20,
                  bottom: 0
                }} data-id="element-2846">
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" vertical={false} data-id="element-2847" />
                      <XAxis dataKey="day" stroke="rgba(255,255,255,0.3)" fontSize={9} tickLine={false} axisLine={false} data-id="element-2848" />
                      <YAxis stroke="rgba(255,255,255,0.3)" fontSize={10} tickLine={false} axisLine={false} domain={[12, 28]} data-id="element-2849" />
                      <Tooltip contentStyle={{
                    backgroundColor: '#0F172A',
                    borderColor: 'rgba(255,255,255,0.1)',
                    borderRadius: '8px',
                    fontSize: '11px'
                  }} labelStyle={{
                    color: 'rgba(255,255,255,0.5)',
                    marginBottom: 4
                  }} data-id="element-2850" />
                      <ReferenceLine x="Today" stroke="rgba(255,255,255,0.2)" strokeDasharray="4 4" label={{
                    value: 'Today',
                    position: 'top',
                    fill: 'rgba(255,255,255,0.4)',
                    fontSize: 9
                  }} data-id="element-2851" />
                      {/* Observed line */}
                      <Line type="monotone" dataKey="observed" name="Observed" stroke="#34d399" strokeWidth={2.5} dot={{
                    r: 2,
                    fill: '#34d399',
                    strokeWidth: 0
                  }} activeDot={{
                    r: 4,
                    fill: '#34d399',
                    strokeWidth: 0
                  }} connectNulls={false} data-id="element-2852" />
                      {/* Dynamic model lines based on selection */}
                      {benchModels.map(mId => {
                    const m = models.find(x => x.id === mId);
                    if (!m) return null;
                    return <Line key={mId} type="monotone" dataKey={mId} name={m.label} stroke={m.color} strokeWidth={1.5} dot={false} activeDot={{
                      r: 3,
                      fill: m.color,
                      strokeWidth: 0
                    }} data-id="element-2853" />;
                  })}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* MAE Rankings */}
              <div className="text-[10px] uppercase tracking-wider text-slate-500 pt-2" data-id="element-2854">
                30-Day MAE Rankings
              </div>
              {benchModels.sort((a, b) => (benchmarkResults[a]?.rank || 99) - (benchmarkResults[b]?.rank || 99)).map((mId, i) => {
            const m = models.find(x => x.id === mId);
            const result = benchmarkResults[mId];
            if (!m || !result) return null;
            const isWinner = i === 0;
            return <motion.div key={mId} initial={{
              opacity: 0,
              x: -10
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: 0.3 + i * 0.1,
              duration: 0.3
            }} className={`flex items-center justify-between p-3 rounded-xl border ${isWinner ? 'bg-white/[0.04] border-white/[0.12]' : 'bg-white/[0.02] border-white/[0.06]'}`} data-id="element-2855">
                      <div className="flex items-center gap-3" data-id="element-2856">
                        <div className="flex items-center justify-center w-6 h-6 rounded-md text-[10px] font-bold" style={{
                  backgroundColor: m.color + '20',
                  color: m.color
                }} data-id="element-2857">
                          #{i + 1}
                        </div>
                        <div data-id="element-2858">
                          <div className="text-sm font-medium" style={{
                    color: m.color
                  }} data-id="element-2859">
                            {m.label}
                          </div>
                          <div className="text-[10px] text-slate-500" data-id="element-2860">
                            {m.desc}
                          </div>
                        </div>
                      </div>
                      <div className="text-right" data-id="element-2861">
                        <div className="text-sm font-bold text-white" data-id="element-2862">
                          {result.mae} GW
                        </div>
                        <div className="text-[10px] text-slate-500" data-id="element-2863">MAE</div>
                      </div>
                      {isWinner && <div className="ml-2 px-2 py-0.5 rounded-md bg-emerald-500/15 border border-emerald-500/25 text-emerald-400 text-[10px] font-semibold" data-id="element-2864">
                          BEST
                        </div>}
                    </motion.div>;
          })}
            </motion.div>}
        </motion.div>
      </motion.div>

      {benchRan ? <InsightCard icon={Trophy} title="Benchmark Complete" data-id="element-2865">
          <strong data-id="element-2866">
            {models.find(m => m.id === benchModels.sort((a, b) => (benchmarkResults[a]?.rank || 99) - (benchmarkResults[b]?.rank || 99))[0])?.label}
          </strong>{' '}
          is the most accurate model for{' '}
          {variables.find(v => v.id === benchVariable)?.label} in{' '}
          {benchRegion}. Use it as your primary source.
        </InsightCard> : <InsightCard icon={BarChart3} title="Custom Benchmarking" data-id="element-2867">
          Select at least 2 models, then run a 30-day verification against
          observations for any region and variable.
        </InsightCard>}

      <CapabilityTag text="Multi-region, multi-variable model benchmarking" data-id="element-2868" />
    </div>;
  const renderStep5 = () => <div className="w-full flex flex-col items-center" data-id="element-2869">
      <AthenaBubble data-id="element-2870">
        <Typewriter text="Here's wind generation alongside day-ahead prices." delay={20} data-id="element-2871" />
      </AthenaBubble>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 1.2,
      duration: 0.6
    }} className="w-full max-w-2xl mt-8 bg-white/[0.02] border border-white/[0.08] rounded-2xl p-6" data-id="element-2872">
        <div className="flex items-center justify-between mb-6" data-id="element-2873">
          <h3 className="text-sm font-medium text-slate-400" data-id="element-2874">
            Wind Gen vs. DA Price
          </h3>
          <div className="flex gap-4 text-xs" data-id="element-2875">
            <span className="flex items-center gap-1.5 text-slate-300" data-id="element-2876">
              <div className="w-2 h-2 rounded-full bg-[#EC4899]" data-id="element-2877" /> Wind (GW)
            </span>
            <span className="flex items-center gap-1.5 text-slate-300" data-id="element-2878">
              <div className="w-2 h-2 rounded-full bg-amber-400" data-id="element-2879" /> Price (€)
            </span>
          </div>
        </div>
        <div className="h-48 w-full" data-id="element-2880">
          <ResponsiveContainer width="100%" height="100%" data-id="element-2881">
            <ComposedChart data={energyData} margin={{
            top: 5,
            right: 0,
            left: -20,
            bottom: 0
          }} data-id="element-2882">
              <defs data-id="element-2883">
                <linearGradient id="colorWind2" x1="0" y1="0" x2="0" y2="1" data-id="element-2884">
                  <stop offset="5%" stopColor="#EC4899" stopOpacity={0.2} data-id="element-2885" />
                  <stop offset="95%" stopColor="#EC4899" stopOpacity={0} data-id="element-2886" />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" vertical={false} data-id="element-2887" />
              <XAxis dataKey="time" stroke="rgba(255,255,255,0.3)" fontSize={11} tickLine={false} axisLine={false} data-id="element-2888" />
              <YAxis yAxisId="left" stroke="rgba(255,255,255,0.3)" fontSize={11} tickLine={false} axisLine={false} data-id="element-2889" />
              <YAxis yAxisId="right" orientation="right" stroke="rgba(255,255,255,0.3)" fontSize={11} tickLine={false} axisLine={false} data-id="element-2890" />
              <Tooltip contentStyle={{
              backgroundColor: '#0F172A',
              borderColor: 'rgba(255,255,255,0.1)',
              borderRadius: '8px'
            }} data-id="element-2891" />
              <Area yAxisId="left" type="monotone" dataKey="wind" name="Wind (GW)" stroke="#EC4899" fill="url(#colorWind2)" data-id="element-2892" />
              <Line yAxisId="right" type="monotone" dataKey="price" name="Price (€/MWh)" stroke="#fbbf24" strokeWidth={2} dot={false} data-id="element-2893" />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </motion.div>

      <InsightCard icon={TrendingUp} title="Price Correlation" data-id="element-2894">
        When wind peaks on Wednesday, prices drop to <strong data-id="element-2895">€45/MWh</strong> —
        a potential trading signal.
      </InsightCard>

      <CapabilityTag text="ENTSO-E market data integration" data-id="element-2896" />
    </div>;
  const EXPLORE_OPTIONS = [{
    icon: Sun,
    title: 'Weather Forecasting',
    desc: 'Explore multi-model forecasts for wind, solar, and temperature',
    path: '/energy',
    color: '#f59e0b'
  }, {
    icon: Trophy,
    title: 'Benchmarking',
    desc: 'Compare model accuracy across regions and variables',
    path: '/stations',
    color: '#8b5cf6'
  }, {
    icon: LayoutGrid,
    title: 'Create a Workspace',
    desc: 'Build a custom dashboard with charts, maps, and tables',
    path: '/workspaces',
    color: '#EC4899'
  }, {
    icon: PenLine,
    title: 'Write your own prompt',
    desc: 'Ask Athena anything — forecasts, analysis, trading signals',
    path: '/athena',
    color: '#38bdf8'
  }, {
    icon: Globe,
    title: 'Interactive Map',
    desc: 'Visualize weather data across Europe on a live map',
    path: '/map',
    color: '#6366f1'
  }];
  const renderStep6 = () => <div className="flex flex-col items-center" data-id="element-2897">
      <div className="text-center mb-8" data-id="element-2898">
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }} className="relative w-16 h-16 mx-auto mb-5" data-id="element-2899">
          <div className="w-16 h-16 rounded-2xl overflow-hidden shadow-[0_0_30px_rgba(236,72,153,0.25)]" data-id="element-2900">
            <img src="https://cdn.magicpatterns.com/uploads/iXnnzvi4wVFko9E21giff4/pasted-image.png" alt="Jua logo" className="w-full h-full object-contain" data-id="element-2901" />
          </div>
          <motion.div initial={{
          scale: 0,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} transition={{
          delay: 0.3,
          duration: 0.3,
          type: 'spring',
          stiffness: 400,
          damping: 15
        }} className="absolute -bottom-1.5 -right-1.5 w-7 h-7 rounded-full bg-emerald-500 flex items-center justify-center shadow-lg shadow-emerald-500/30 ring-2 ring-[#0B1120]" data-id="element-2902">
            <Check size={14} className="text-white" strokeWidth={3} data-id="element-2903" />
          </motion.div>
        </motion.div>
        <h1 className="text-3xl font-bold text-white tracking-tight mb-2" data-id="element-2904">
          Where would you like to go?
        </h1>
        <p className="text-slate-400 text-base max-w-md mx-auto" data-id="element-2905">
          Jump straight into any part of the platform, or let Athena guide you.
        </p>
      </div>

      <div className="w-full max-w-2xl grid grid-cols-2 md:grid-cols-3 gap-3" data-id="element-2906">
        {EXPLORE_OPTIONS.map((opt, i) => <motion.button key={opt.title} initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.15 + i * 0.07,
        duration: 0.35
      }} onClick={() => {
        handleComplete();
        navigate(opt.path);
      }} className="group flex flex-col items-start gap-3 p-4 rounded-xl bg-white/[0.03] border border-white/[0.08] hover:bg-white/[0.07] hover:border-white/[0.18] transition-all text-left" data-id="element-2907">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center border backdrop-blur-sm ${isLight ? 'bg-black/[0.06] border-black/[0.08] shadow-[inset_0_1px_0_rgba(0,0,0,0.04)]' : 'bg-white/[0.06] border-white/[0.08] shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]'}`} data-id="element-2908">
              <opt.icon size={18} className={isLight ? 'text-slate-500/70 group-hover:text-slate-700' : 'text-white/70 group-hover:text-white/90'} data-id="element-2909" />
            </div>
            <div data-id="element-2910">
              <p className="text-sm font-medium text-white/90 group-hover:text-white transition-colors mb-0.5" data-id="element-2911">
                {opt.title}
              </p>
              <p className="text-[11px] text-slate-500 group-hover:text-slate-400 transition-colors leading-relaxed" data-id="element-2912">
                {opt.desc}
              </p>
            </div>
            <ArrowRight size={14} className="text-slate-600 group-hover:text-white/50 group-hover:translate-x-0.5 transition-all mt-auto" data-id="element-2913" />
          </motion.button>)}
      </div>
    </div>;
  const steps = [renderStep1, renderStep2, renderStep3, renderStepAlert, renderStep5, renderStep6];
  const ctaText = ["Let's start with your market", 'Want to see how confident we are?', 'Set up an alert for this?', 'Want to connect this to energy prices?', "I'm ready to explore", 'Go to Overview'];
  return <div className={embedded ? 'jua-athena-discovery h-full w-full flex flex-col relative overflow-hidden' : 'jua-athena-discovery min-h-screen bg-[#0B1120] text-slate-100 font-sans selection:bg-pink-500/30 relative overflow-hidden flex flex-col'} data-id="element-2914">
      {/* Background Gradients - only in standalone mode */}
      {!embedded && <>
          <div className="absolute inset-x-0 top-0 h-[800px] bg-gradient-to-b from-[#1a0a12] via-[#0B1120] to-[#0B1120] pointer-events-none" data-id="element-2915" />
          <div className="absolute top-[-200px] left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-pink-500/10 rounded-full blur-[120px] pointer-events-none" data-id="element-2916" />
          <div className="absolute top-[-100px] right-0 w-[500px] h-[500px] bg-purple-500/10 rounded-full blur-[100px] pointer-events-none" data-id="element-2917" />
        </>}

      {/* Header */}
      {embedded ? <header className="relative z-10 flex items-center justify-center px-8 py-3" data-id="element-2918">
          <div className="flex gap-1.5" data-id="element-2919">
            {[0, 1, 2, 3, 4, 5].map(i => <div key={i} className={`w-1.5 h-1.5 rounded-full transition-colors duration-300 ${i <= step ? 'bg-[#EC4899]' : 'bg-white/[0.15]'}`} data-id="element-2920" />)}
          </div>
          <button onClick={handleComplete} className="absolute right-8 text-xs font-medium text-slate-500 hover:text-slate-300 transition-colors" data-id="element-2921">
            Skip →
          </button>
        </header> : <header className="relative z-10 flex items-center justify-between px-8 py-6" data-id="element-2922">
          <div className="flex items-center gap-2 opacity-80" data-id="element-2923">
            <img src="https://cdn.magicpatterns.com/uploads/iXnnzvi4wVFko9E21giff4/pasted-image.png" alt="Jua logo" className="w-7 h-7 object-contain flex-shrink-0" data-id="element-2924" />
            <span className="text-lg font-light tracking-tight text-white" data-id="element-2925">
              Jua<span className="text-[#EC4899]" data-id="element-2926">.ai</span>
            </span>
          </div>

          {/* Progress Indicator */}
          <div className="flex flex-col items-center gap-2" data-id="element-2927">
            <div className="w-48 h-1 bg-white/[0.08] rounded-full overflow-hidden" data-id="element-2928">
              <motion.div className="h-full bg-[#EC4899]" initial={{
            width: '16.6%'
          }} animate={{
            width: `${(step + 1) / 6 * 100}%`
          }} transition={{
            duration: 0.3,
            ease: 'easeInOut'
          }} data-id="element-2929" />
            </div>
            <div className="flex gap-1.5" data-id="element-2930">
              {[0, 1, 2, 3, 4, 5].map(i => <div key={i} className={`w-1.5 h-1.5 rounded-full transition-colors duration-300 ${i <= step ? 'bg-[#EC4899]' : 'bg-white/[0.15]'}`} data-id="element-2931" />)}
            </div>
          </div>

          <button onClick={handleComplete} className="p-1.5 rounded-lg text-slate-500 hover:text-white hover:bg-white/[0.08] transition-colors" title="Close" data-id="element-2932">
            <X size={18} data-id="element-2933" />
          </button>
        </header>}

      {/* Main Content Area */}
      <main ref={mainRef} className="relative z-10 flex-1 overflow-y-auto px-6 py-8" data-id="element-2934">
        <div className="w-full max-w-4xl relative min-h-[500px] flex flex-col mx-auto" data-id="element-2935">
          <AnimatePresence mode="wait" custom={direction} data-id="element-2936">
            <motion.div key={step} custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
            type: 'spring',
            stiffness: 300,
            damping: 30
          }} className="w-full" data-id="element-2937">
              {steps[step]()}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>

      {/* Footer Navigation */}
      <footer className={`relative z-10 px-8 ${embedded ? 'py-4' : 'py-6'} flex items-center justify-between w-full border-t border-white/[0.05]`} data-id="element-2938">
        <div data-id="element-2939">
          {step > 0 && <Button variant="ghost" onClick={() => goToStep(step - 1)} className="text-slate-400 hover:text-white gap-2" data-id="element-2940">
              <ArrowLeft size={16} data-id="element-2941" /> Back
            </Button>}
        </div>

        <Button onClick={() => {
        if (step === 5) handleComplete();else goToStep(step + 1);
      }} className="gap-2 px-6 shadow-lg shadow-pink-500/20" data-id="element-2942">
          {ctaText[step]} <ArrowRight size={16} data-id="element-2943" />
        </Button>
      </footer>
    </div>;
}
export function AthenaDiscoveryEmbed() {
  return <AthenaDiscoveryView embedded={true} data-id="element-2944" />;
}